// Adapter functions to convert stored question objects into runtime props
import get from 'lodash/get';

export function buildImageProps(q) {
  if (!q) return { items: [], targets: [], answers: [] };
  const items = (q.choices || []).map((choice, i) => {
    const src = get(choice, 'c', get(choice, 'src', choice));
    return { id: `img${i}`, src, label: `Choice ${i}` };
  });
  const targets = (q.matches || []).map((label, i) => {
    const expectedIndex = Array.isArray(q.answers) && typeof q.answers[i] !== 'undefined' ? q.answers[i] : null;
    const expectedRaw = expectedIndex != null && q.choices && q.choices[expectedIndex] ? q.choices[expectedIndex] : null;
    const expectedSrc = get(expectedRaw, 'c', get(expectedRaw, 'src', expectedRaw));
    return {
      id: `q${i}`,
      label: label || '',
      expectedIndex,
      expectedSrc,
    };
  });
  return { items, targets, answers: Array.isArray(q.answers) ? q.answers.slice() : [] };
}

export function buildSentenceProps(q) {
  if (!q) return { tokens: [], sentence: [], answers: [] };
  const tokens = (q.choices || []).map((choice, i) => {
    const text = get(choice, 'c', choice);
    return { id: `t${i}`, text };
  });
  const rawAnswers = Array.isArray(q.answers) ? q.answers : [];
  const activeAnswerIndices = rawAnswers
    .map((ans, idx) => ({ ans, idx }))
    .filter(({ ans }) => ans !== null && typeof ans !== 'undefined' && ans !== '')
    .map(({ idx }) => idx);
  const count = activeAnswerIndices.length;
  const sentence = [];
  const template = typeof q.sentenceTemplate === 'string'
    ? q.sentenceTemplate
    : '';

  if (template.trim() !== '' && count > 0) {
    const expectedSeq = [];
    for (let i = 0; i < count; i += 1) {
      const answerPos = activeAnswerIndices[i];
      const ansIdx = rawAnswers[answerPos];
      if (ansIdx == null || Number.isNaN(Number(ansIdx))) {
        expectedSeq.push('');
      } else {
        const raw = q.choices && q.choices[Number(ansIdx)];
        const tokenText = get(raw, 'c', raw);
        expectedSeq.push(tokenText == null ? '' : String(tokenText));
      }
    }

    // compare template against expected sequence and extract punctuation/text between slots
    const separators = Array(count + 1).fill('');
    let tplPtr = 0;
    let matched = true;

    for (let i = 0; i < count; i += 1) {
      const token = expectedSeq[i] || '';
      if (token === '') {
        matched = false;
        break;
      }

      const foundAt = template.indexOf(token, tplPtr);
      if (foundAt === -1) {
        matched = false;
        break;
      }

      separators[i] = template.slice(tplPtr, foundAt);
      tplPtr = foundAt + token.length;
    }

    if (matched) {
      separators[count] = template.slice(tplPtr);
      for (let i = 0; i < count; i += 1) {
        if (separators[i]) sentence.push({ text: separators[i] });
        const answerPos = activeAnswerIndices[i];
        sentence.push({
          type: 'slot',
          id: `s${i}`,
          placeholder: `詞 ${i + 1}`,
          expectedIndex: answerPos,
          answerIndex: answerPos,
        });
      }
      if (separators[count]) sentence.push({ text: separators[count] });
    }
  }

  if (sentence.length === 0) {
    for (let i = 0; i < count; i += 1) {
      const answerPos = activeAnswerIndices[i];
      sentence.push({
        type: 'slot',
        id: `s${i}`,
        placeholder: `詞 ${i + 1}`,
        expectedIndex: answerPos,
        answerIndex: answerPos,
      });
    }
    sentence.push({ text: '。' });
  }

  return { tokens, sentence, answers: Array.isArray(q.answers) ? q.answers.slice() : [] };
}

export function buildIdiomsProps(q) {
  if (!q) {
    return {
      tokens: [], gridSize: [5, 5], slots: [], answers: [], idioms: [],
    };
  }
  const tokens = Array.isArray(q.tokens) ? q.tokens.map((t, i) => ({ id: `tk${i}`, text: t })) : [];
  const rows = (q.gridSize && q.gridSize[0]) || 5;
  const cols = (q.gridSize && q.gridSize[1]) || 5;

  const cellMap = {};
  const expectedMap = {};
  (Array.isArray(q.idioms) ? q.idioms : []).forEach((idm) => {
    const start = Array.isArray(idm.start) ? idm.start : [0, 0];
    const end = Array.isArray(idm.end) ? idm.end : start;
    const dir = idm.direction || (start[0] === end[0] ? 'horizontal' : 'vertical');
    const pattern = Array.isArray(idm.pattern) ? idm.pattern : [];
    if (dir === 'horizontal') {
      const r = start[0];
      const sc = Math.min(start[1], end[1]);
      for (let i = 0; i < pattern.length; i += 1) {
        const c = sc + i;
        const k = `${r},${c}`;
        cellMap[k] = true;
        expectedMap[k] = pattern[i] || '';
      }
    } else {
      const c = start[1];
      const sr = Math.min(start[0], end[0]);
      for (let i = 0; i < pattern.length; i += 1) {
        const r = sr + i;
        const k = `${r},${c}`;
        cellMap[k] = true;
        expectedMap[k] = pattern[i] || '';
      }
    }
  });

  const slots = [];
  let idx = 0;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const k = `${r},${c}`;
      if (cellMap[k]) {
        slots.push({
          r, c, id: `s${idx}`, idx, expected: expectedMap[k] || '',
        });
        idx += 1;
      }
    }
  }

  const idiomSummaries = [];
  (Array.isArray(q.idioms) ? q.idioms : []).forEach((idm) => {
    const start = Array.isArray(idm.start) ? idm.start : [0, 0];
    const end = Array.isArray(idm.end) ? idm.end : start;
    const dir = idm.direction || (start[0] === end[0] ? 'horizontal' : 'vertical');
    const pattern = Array.isArray(idm.pattern) ? idm.pattern : [];
    const slotIndices = [];
    if (dir === 'horizontal') {
      const r = start[0];
      const sc = Math.min(start[1], end[1]);
      for (let i = 0; i < pattern.length; i += 1) {
        const c = sc + i;
        const s = slots.find((x) => x.r === r && x.c === c);
        if (s) slotIndices.push(s.idx);
      }
    } else {
      const c = start[1];
      const sr = Math.min(start[0], end[0]);
      for (let i = 0; i < pattern.length; i += 1) {
        const r = sr + i;
        const s = slots.find((x) => x.r === r && x.c === c);
        if (s) slotIndices.push(s.idx);
      }
    }
    idiomSummaries.push({ slotIndices, answers: Array.isArray(idm.answers) ? idm.answers.slice() : [] });
  });

  return {
    tokens,
    gridSize: [rows, cols],
    slots,
    answers: Array.isArray(q.answers) ? q.answers.slice() : [],
    idioms: idiomSummaries,
  };
}

export default { buildImageProps, buildSentenceProps, buildIdiomsProps };
